/**
 * Utilidad compartida de logging para el proyecto
 * 
 * Esta utilidad proporciona funciones de logging que:
 * - Solo se ejecutan en producción (no en tests)
 * - No afectan el coverage de SonarQube
 * - Proporcionan logs consistentes en toda la aplicación
 */

/**
 * Función de logging que se ejecuta en cualquier ambiente
 * Excluida de coverage para no afectar las métricas de SonarQube
 * 
 * @param args - Argumentos a loggear
 */
export function debugLog(...args: unknown[]): void {
  // Loggear en cualquier ambiente
  // eslint-disable-next-line no-console
  console.log(...args);
}

/**
 * Función de logging de errores que se ejecuta en cualquier ambiente
 * Excluida de coverage para no afectar las métricas de SonarQube
 * 
 * @param args - Argumentos de error a loggear
 */
export function debugError(...args: unknown[]): void {
  // Loggear en cualquier ambiente
  // eslint-disable-next-line no-console
  console.error(...args);
}

/**
 * Función de logging de advertencias que se ejecuta en cualquier ambiente
 * Excluida de coverage para no afectar las métricas de SonarQube
 * 
 * @param args - Argumentos de advertencia a loggear
 */
export function debugWarn(...args: unknown[]): void {
  // Loggear en cualquier ambiente
  // eslint-disable-next-line no-console
  console.warn(...args);
}

/**
 * Función de logging de información que se ejecuta en cualquier ambiente
 * Excluida de coverage para no afectar las métricas de SonarQube
 * 
 * @param args - Argumentos de información a loggear
 */
export function debugInfo(...args: unknown[]): void {
  // Loggear en cualquier ambiente
  // eslint-disable-next-line no-console
  console.info(...args);
}

/**
 * Función de logging de debug que se ejecuta en cualquier ambiente
 * Excluida de coverage para no afectar las métricas de SonarQube
 * 
 * @param args - Argumentos de debug a loggear
 */
export function debugDebug(...args: unknown[]): void {
  // Loggear en cualquier ambiente
  // eslint-disable-next-line no-console
  console.debug(...args);
}

/**
 * Función de logging de tabla que se ejecuta en cualquier ambiente
 * Excluida de coverage para no afectar las métricas de SonarQube
 * 
 * @param args - Argumentos de tabla a loggear
 */
export function debugTable(...args: unknown[]): void {
  // Loggear en cualquier ambiente
  // eslint-disable-next-line no-console
  console.table(...args);
}

/**
 * Función de logging de grupo que se ejecuta en cualquier ambiente
 * Excluida de coverage para no afectar las métricas de SonarQube
 * 
 * @param label - Etiqueta del grupo
 * @param args - Argumentos del grupo a loggear
 */
export function debugGroup(label: string, ...args: unknown[]): void {
  // Loggear en cualquier ambiente
  // eslint-disable-next-line no-console
  console.group(label, ...args);
}

/**
 * Función de logging de fin de grupo que se ejecuta en cualquier ambiente
 * Excluida de coverage para no afectar las métricas de SonarQube
 */
export function debugGroupEnd(): void {
  // Loggear en cualquier ambiente
  // eslint-disable-next-line no-console
  console.groupEnd();
}

/**
 * Función de logging de tiempo que se ejecuta en cualquier ambiente
 * Excluida de coverage para no afectar las métricas de SonarQube
 * 
 * @param label - Etiqueta del timer
 */
export function debugTime(label: string): void {
  // Loggear en cualquier ambiente
  // eslint-disable-next-line no-console
  console.time(label);
}

/**
 * Función de logging de fin de tiempo que se ejecuta en cualquier ambiente
 * Excluida de coverage para no afectar las métricas de SonarQube
 * 
 * @param label - Etiqueta del timer
 */
export function debugTimeEnd(label: string): void {
  // Loggear en cualquier ambiente
  // eslint-disable-next-line no-console
  console.timeEnd(label);
}
